import { useMemo } from 'react';
import { useShowEnsGoodsBanner } from '../../../../common/goods/useGoods';
import type { DetailedBooking } from '../../../../common/graphql/fragments/gql/DetailedBooking';
import type { UpdateSubmitInformation } from '../../../../common/update-information/utils/getUpdateSubmitInformation';
import { resolveAnimalsInformationText } from '../../../../common/utils/animalsUtils';
import { transformBookingToMinimalBooking } from '../../../../common/utils/minimalBooking';
import { shouldShowPluginInformationText } from '../../../../common/utils/temperatureUtil';
import { resolveArrivalNotepadApprovalInfo } from '../utils/arrivalNotepadInformation';
import { resolveAutoCancellationInformation } from '../utils/autoCancellationUtils';
import {
  getHazardousGoodsApprovalInfo,
  getLoadingPreferenceApprovalInfo,
} from '../utils/detailsUtils';
import { shouldShowHazGoodsPaymentInformation } from '../utils/showPaymentUtil';
import { BookingInformationMessages } from './BookingInformationMessages';

interface InfoMessageDeciderProps {
  booking: DetailedBooking;
  updateSubmitInformation: UpdateSubmitInformation;
}

export const InfoMessageDecider = ({
  booking,
  updateSubmitInformation,
}: InfoMessageDeciderProps) => {
  const loadingPreferenceApprovalInfo = getLoadingPreferenceApprovalInfo(
    booking.loadingPreference,
    booking.loadingPreferenceStatusCode,
  );
  const hazardousGoodsApprovalInfo = getHazardousGoodsApprovalInfo(
    booking.hazardousGoods,
    booking.hazardousGoodsApproved,
    booking.bookingStatus.status,
  );
  const livestockInfoText = resolveAnimalsInformationText(booking.livestock);
  const arrivalNotepadInfo = resolveArrivalNotepadApprovalInfo(booking.arrivalNotepadApplied);
  const showHazGoodsPaymentInformation = shouldShowHazGoodsPaymentInformation(booking);
  const autoCancellation = resolveAutoCancellationInformation(
    updateSubmitInformation,
    booking.autoCancellation,
  );
  const minimalBooking = useMemo(() => transformBookingToMinimalBooking(booking), [booking]);

  const showPluginInformation = shouldShowPluginInformationText(
    booking.noOfPlugins,
    booking.pluginTemperature,
  );

  const showEnsGoodsBanner = useShowEnsGoodsBanner(minimalBooking, {
    ensConfirmed: booking.importReference.ensConfirmed,
  });

  if (
    !loadingPreferenceApprovalInfo &&
    !hazardousGoodsApprovalInfo &&
    !livestockInfoText &&
    !arrivalNotepadInfo &&
    !autoCancellation &&
    !showPluginInformation &&
    !showEnsGoodsBanner
  ) {
    return null;
  }

  return (
    <BookingInformationMessages
      arrivalNotepadInfo={arrivalNotepadInfo}
      autoCancellation={autoCancellation}
      bookingNo={booking.bookingNo}
      hazardousGoodsApproved={booking.hazardousGoodsApproved}
      hazardousGoodsApprovalInfo={hazardousGoodsApprovalInfo}
      livestockInfoText={livestockInfoText}
      loadingPreferenceApprovalInfo={loadingPreferenceApprovalInfo}
      showHazGoodsPaymentInformation={showHazGoodsPaymentInformation}
      showPluginInformation={showPluginInformation}
      showEnsGoodsBanner={showEnsGoodsBanner}
    />
  );
};
